import { CircularProgress } from '@mui/material'
import {
	GET_FM_PARTNER_LIST_FOLLOWERS_PARTNERS,
	getFMPartnerListPartners
} from 'api'
import { BoxPartners, Button, Input } from 'components'
import { useSearchDebounce } from 'hooks'
import {
	FMPartnerListPartnersItem,
	Icons,
	SharePartnerInputs,
	SharePartnerProps
} from 'interfaces'
import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import Cookies from 'universal-cookie'

export const SharePartner: React.FC<SharePartnerProps> = ({
	totalData,
	setTotalData,
	onClickClose,
	setSharePartner,
	setSlideAudience
}): JSX.Element => {
	const cookies = new Cookies()
	const { ref, inView } = useInView()
	const userId = cookies.get('userId')
	const organizationId = cookies.get('organization_id')
	const [partnersSelected, setPartnersSelected] = useState<number[]>(() => [
		...totalData.delivery_enhancements.audience.organizations
	])

	const { watch, register, handleSubmit } = useForm<SharePartnerInputs>({
		mode: 'all'
	})

	const watchSearch = watch('search')
	const { inputSearch, handleChangeDebounce } = useSearchDebounce(watchSearch)
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useInfiniteQuery(
			[
				GET_FM_PARTNER_LIST_FOLLOWERS_PARTNERS,
				userId,
				organizationId,
				inputSearch
			],
			({ pageParam = 1 }) =>
				getFMPartnerListPartners(organizationId, {
					page: pageParam,
					search: inputSearch
				}),
			{
				getPreviousPageParam: firstPage =>
					firstPage?.page ? firstPage.page : undefined,
				getNextPageParam: lastPage =>
					lastPage && lastPage.page < lastPage.pages
						? lastPage.page + 1
						: undefined
			}
		)

	useEffect(() => {
		if (inView && hasNextPage) fetchNextPage()
	}, [inView])

	const listOrganizations = useMemo(
		() => data?.pages.flatMap(page => page?.items ?? []) ?? [],
		[data]
	)

	const handleClickPartner = (partner: FMPartnerListPartnersItem): void => {
		const partnerId = parseInt(partner.partner_id, 10)
		if (partnersSelected.includes(partnerId)) {
			setPartnersSelected(partnersSelected.filter(item => item !== partnerId))
		} else {
			setPartnersSelected([...partnersSelected, partnerId])
		}
	}

	const onSubmit: SubmitHandler<SharePartnerInputs> = () => {
		setTotalData({
			...totalData,
			delivery_enhancements: {
				...totalData.delivery_enhancements,
				audience: {
					...totalData.delivery_enhancements.audience,
					organizations: partnersSelected
				}
			}
		})
		setSharePartner(false)
		setSlideAudience(true)
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="pt-5 h-full">
			<div className="h-[93%]">
				<Input
					name="search"
					className="mb-4"
					borderFull={false}
					register={register}
					leftIcon={Icons.search}
					inputClassName="rounded-[10px]"
					placeholder="Search your partners"
					onChangeCustom={handleChangeDebounce}
				/>
				<div className="overflow-y-scroll  hide-scroll-bar max-h-[90%] pt-3">
					{isLoading ? (
						<div className="flex items-center justify-center w-full h-[400px] py-2 px-1 ">
							<CircularProgress disableShrink />
						</div>
					) : (
						listOrganizations?.map(item => (
							<BoxPartners
								key={item.partner_id}
								id={item.partner_id}
								image={item.icon_image_url}
								title={item.partner_name}
								itemsSeleted={partnersSelected}
								onClick={() => handleClickPartner(item)}
							/>
						))
					)}
					<div ref={ref}>
						{isFetchingNextPage && <CircularProgress disableShrink />}
					</div>
				</div>
			</div>
			<div className="flex items-center justify-between">
				<Button
					type="button"
					label="Cancel"
					color="Transparent"
					className="!min-w-[130px]"
					onClick={onClickClose}
				/>
				<Button
					label="Save"
					type="submit"
					color="Primary"
					className="!min-w-[130px]"
				/>
			</div>
		</form>
	)
}
